import type { WorkReportBillingState, WorkReportSummaryDto } from '@repo/api';
import type { WorkReportBillingViewer } from '@repo/utils';
import type { VariantProps } from 'class-variance-authority';
import type { badgeVariants } from '@repo/ui/components/ui/badge';

type Tone = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export const WORK_REPORT_STATUS_TONE: Record<
  WorkReportSummaryDto['status'],
  Tone
> = {
  DRAFT: 'neutral',
  SUBMITTED: 'warning',
  APPROVED: 'success',
  CANCELLED: 'muted',
};

/**
 * Bozza o da approvare: il contenuto si modifica ancora e il rapportino si
 * approva (lo specchio di `EDITABLE_WORK_REPORT_STATUSES` del backend). Una
 * bozza si approva direttamente, senza passare dall'invio.
 */
export const isEditableWorkReportStatus = (
  status: WorkReportSummaryDto['status']
): boolean => status === 'DRAFT' || status === 'SUBMITTED';

export const BILLING_STATE_TONE: Record<WorkReportBillingState, Tone> = {
  NONE: 'neutral',
  TO_INVOICE: 'warning',
  PARTIAL: 'info',
  DRAFT_INVOICE: 'proposal',
  INVOICED: 'success',
  CREDITED: 'danger',
};

/**
 * Chiave i18n di uno stato di fatturazione per chi guarda: badge, chip e
 * opzioni del filtro. `viewer.documents` è `canAccessDocuments` di
 * `usePermissions` (bypass OWNER compreso). «Parzialmente in fattura» vale
 * per tutti: PARTIAL comprende anche gli addebiti su bozze di fattura, quindi
 * «Parzialmente fatturato» sarebbe falso. «Fatturato» lo vede solo chi vede
 * le Fatture: per gli altri il server fonde le bozze nell'INVOICED
 * (`toViewerWorkReportBillingState`) e diventa «In fattura».
 */
export function billingStateLabelKey(
  state: WorkReportBillingState,
  viewer: WorkReportBillingViewer
): string {
  if (state === 'PARTIAL') return 'work_reports.billing_in_invoice.PARTIAL';
  return !viewer.documents && state === 'INVOICED'
    ? 'work_reports.billing_in_invoice.INVOICED'
    : `work_reports.billing.${state}`;
}

/**
 * Chiave del badge «Fatturazione»: un rapportino approvato senza addebiti ma
 * con tempo lavorato è tutto incluso nel canone (tavola 15a), una bozza non
 * ha ancora addebiti.
 */
export function billingLabelKey(
  report: Pick<
    WorkReportSummaryDto,
    'status' | 'billingState' | 'effectiveMinutes'
  >,
  viewer: WorkReportBillingViewer
): string | null {
  if (report.status !== 'APPROVED') return null;
  if (report.billingState === 'NONE') {
    return report.effectiveMinutes > 0
      ? 'work_reports.billing.INCLUDED'
      : 'work_reports.billing.NONE';
  }
  return billingStateLabelKey(report.billingState, viewer);
}

/** Stato di una riga addebito come la mostra il dettaglio. */
export type ChargeDisplayStatus =
  | 'OPEN'
  | 'INVOICED'
  | 'DRAFT_INVOICE'
  | 'CREDITED'
  | 'VOIDED';

export const CHARGE_STATUS_TONE: Record<ChargeDisplayStatus, Tone> = {
  OPEN: 'warning',
  INVOICED: 'success',
  DRAFT_INVOICE: 'proposal',
  CREDITED: 'danger',
  VOIDED: 'muted',
};

/**
 * Su una fattura stornata per intero o annullata l'addebito è «Stornato»
 * (anche per chi non vede le Fatture: il server manda il flag senza
 * numero); su una bozza non è «Fatturato».
 */
export function chargeDisplayStatusOf(charge: {
  status: 'OPEN' | 'INVOICED' | 'VOIDED';
  onDraftInvoice: boolean;
  onCreditedInvoice: boolean;
}): ChargeDisplayStatus {
  if (charge.status === 'VOIDED') return 'VOIDED';
  if (charge.onCreditedInvoice) return 'CREDITED';
  return charge.onDraftInvoice ? 'DRAFT_INVOICE' : charge.status;
}

/** Chiave dell'etichetta: senza Fatture «In fattura», bozze comprese. */
export function chargeStatusLabelKey(
  status: ChargeDisplayStatus,
  viewer: WorkReportBillingViewer
): string {
  return !viewer.documents && status === 'INVOICED'
    ? 'work_reports.charge_status_in_invoice.INVOICED'
    : `work_reports.charge_status.${status}`;
}
